// cherry-pick lodash methods (the right way). DO NOT use import from 'lodash'.
import each from 'lodash/each';
import get from 'lodash/get';
import isBoolean from 'lodash/isBoolean';
import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';
import omit from 'lodash/omit';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { browserName, deviceType, isMobile } from 'react-device-detect';

import { LayoutContext } from './LayoutContext';
import { RegionShape } from './RegionShape.proptypes';
import { RegionType } from './RegionType.enum';
import { ScreenLayout } from './ScreenLayout.enum';

// 300 ms resize buffer
const RESIZE_BUFFER = 300;

const DeviceOrientation = Object.freeze({
	Portrait: 'portrait',
	Landscape: 'landscape',
});

export class Viewport extends Component {
	constructor(props) {
		super(props);

		const screenLayout = this.calculateScreenLayout(window.innerWidth);
		const deviceOrientation = this.calculateDeviceOrientation();

		this.state = {
			// viewport layout
			layout: this.processLayout(props.layout),

			// browser name
			browserName,

			// platform ('browser', 'mobile', or 'tablet')
			deviceType: deviceType === 'browser' ? 'desktop' : deviceType,

			// 'portrait' or 'landscape'
			deviceOrientation,

			// `true` when deviceType is either mobile or tablet
			isMobile,

			// `true` when deviceType is neither mobile or tablet
			isDesktop: !isMobile,

			// `true` when screen size is smaller than 768px
			isPhoneLayout: screenLayout === ScreenLayout.Phone,

			// `true` when screen size is between 768px and 992px
			isTabletLayout: screenLayout === ScreenLayout.Tablet,

			// `true` when screen size is greater than 992px
			isDesktopLayout: screenLayout === ScreenLayout.Desktop,

			// based on screen size breakpoints ('phone', 'tablet', or 'desktop')
			screenLayout,

			// subscribers that listen to 'layoutUpdate' event
			subscribers: {},

			/**
			|------------------------------------------------------------------------
			| Create an object composed of the picked object properties.
			| @param {RegionType} region: `null` to return the whole layout object.
			| @return {object|null}
			|------------------------------------------------------------------------
			*/
			getLayout: region => {
				const { layout } = { ...this.state };

				return region ? get(layout, region) : layout;
			},

			/**
			|------------------------------------------------------------------------
			| Return the region height from layout config object.
			| @param {RegionType} region
			| @return {number}
			|------------------------------------------------------------------------
			*/
			getRegionHeight: region => {
				const hiddenHeight = 0;
				const { getLayout } = this.state;
				const regionLayout = getLayout(region);
				const { isHidden = false, height } = regionLayout;

				if (isHidden) {
					return hiddenHeight;
				} else {
					return isNumber(height) ? height : 0;
				}
			},

			/**
			|------------------------------------------------------------------------
			| Return the region width based on its collapsed flag and width config.
			| @param {RegionType} region
			| @return {number}
			|------------------------------------------------------------------------
			*/
			getRegionWidth: region => {
				const collapsedWidth = 40; // hardcoded
				const hiddenWidth = 0;
				const { getLayout } = this.state;
				const regionLayout = getLayout(region) || {};
				const { width, isCollapsed = false, isHidden = false } = regionLayout;

				if (isHidden === true) {
					return hiddenWidth;
				} else if (isCollapsed === true) {
					return collapsedWidth;
				} else {
					return isNumber(width) ? width : 0;
				}
			},

			// collapse the CollapsibleSidePanel component
			collapseSidePanel: region => {
				if (region === RegionType.West || region === RegionType.East) {
					const { getLayout } = this.state;
					const layout = getLayout();
					const regionLayout = layout[region];

					if (regionLayout) {
						// toggle collapsed state
						regionLayout.isCollapsed = !regionLayout.isCollapsed;

						// to re-calculate dimensions for all regions
						this.setState({ layout }, this.notifySubscribers);
					}
				}
			},

			setHidden: (region, isHidden) => {
				const { layout } = { ...this.state };

				if (region === RegionType.East) {
					if (isObject(layout[region])) {
						// set isHidden state
						layout[region].isHidden = isBoolean(isHidden) ? isHidden : true;

						if (isHidden === false) {
							layout[region].isCollapsed = false;
						}

						// to re-calculate dimensions for all regions
						this.setState({ layout: { ...layout } }, this.notifySubscribers);
					}
				}
			},

			// resize the CollapsibleSidePanel component
			resize: ({ region, posX: newWidth }) => {
				if (region === RegionType.West || region === RegionType.East) {
					const { getLayout } = this.state;
					const layout = getLayout();
					const regionLayout = layout[region];

					if (regionLayout) {
						// toggle collapsed state
						if (region === RegionType.West) {
							regionLayout.width = newWidth;
						} else {
							regionLayout.width = window.innerWidth - newWidth;
						}

						// to re-calculate dimensions for all regions
						this.setState({ layout }, this.notifySubscribers);
					}
				}
			},

			/**
			|------------------------------------------------------------------------
			| Subscribe 'updateLayout' event so that the subscribe can update itself
			| if needed.
			| @param {string} subscriber An unique Id.
			| @param {function} callback
			|------------------------------------------------------------------------
			*/
			subscribeLayoutUpdateEvent: (subscriber, callback) => {
				const { subscribers } = this.state;
				this.setState({
					subscribers: { ...subscribers, [subscriber]: callback },
				});
			},

			/**
			|------------------------------------------------------------------------
			| Unsubscribe 'updateLayout' event.
			| @param {string} subscriber An unique Id.
			|------------------------------------------------------------------------
			*/
			unsubscribeLayoutUpdateEvent: subscriber => {
				const { subscribers } = this.state;
				this.setState({
					subscribers: omit(subscribers, subscriber),
				});
			},
		};

		window.addEventListener('resize', this.resizeHandler);

		// input buffer for current page input field
		this.resizeBuffer = null;
	}

	componentDidMount() {
		const { getLayout } = this.state;
		const layout = getLayout();
		const regionLayout = layout[RegionType.West];

		// collapse the west region panel when it is in mobile mode
		if (regionLayout && isMobile && deviceType === 'mobile') {
			// collapse the west region
			regionLayout.isCollapsed = true;

			// to re-calculate dimensions for all regions
			this.setState({ layout });
		}
	}

	componentWillUnmount() {
		if (this.resizeBuffer) {
			clearTimeout(this.resizeBuffer);
			this.resizeBuffer = null;
		}

		window.removeEventListener('resize', this.resizeHandler);
		window.removeEventListener('orientationchange', this.resizeHandler);
	}

	/**
	 * Process the initial layout config and modify certain region config per isMobile flag.
	 * @param {object} initLayout The layout config for the viewport.
	 * @returns {object}
	 */
	processLayout = initLayout => {
		const layout = isObject(initLayout) ? initLayout : {};

		return isObject(layout[RegionType.West])
			? {
					...layout,
					[RegionType.West]: {
						...layout[RegionType.West],

						// automatically collapse west region on mobile platform (phone or tablet)
						isCollapsed: isMobile === true,
					},
				}
			: layout;
	};

	/**
	 * Calculate the screen layout based on the viewport width.
	 * @params {width} Viewport width.
	 * @return {ScreenLayout}
	 */
	calculateScreenLayout = width => {
		// 768px is the same breakpoint for .p-sm-* selector in primeflex (.p-grid)
		if (width < 768) {
			return ScreenLayout.Phone;
		}

		// 992px is the same breakpoint for .p-md-* selector in  primeflex (.p-grid)
		if (width >= 768 && width < 992) {
			return ScreenLayout.Tablet;
		}

		return ScreenLayout.Desktop;
	};

	/**
	 * Calculate whether screen layout is in portrait or landscape.
	 * @return {DeviceOrientation}
	 */
	calculateDeviceOrientation = () =>
		window.matchMedia('(orientation: portrait)').matches
			? DeviceOrientation.Portrait
			: DeviceOrientation.Landscape;

	resizeHandler = () => {
		if (this.resizeBuffer) {
			clearTimeout(this.resizeBuffer);
			this.resizeBuffer = null;
		}

		this.resizeBuffer = setTimeout(() => {
			const { screenLayout, deviceOrientation } = this.state;
			const newScreenLayout = this.calculateScreenLayout(window.innerWidth);
			const newDeviceOrientation = this.calculateDeviceOrientation();

			if (
				newScreenLayout !== screenLayout ||
				newDeviceOrientation !== deviceOrientation
			) {
				this.setState(
					{
						screenLayout: newScreenLayout,
						deviceOrientation: newDeviceOrientation,
						isPhoneLayout: newScreenLayout === ScreenLayout.Phone,
						isTabletLayout: newScreenLayout === ScreenLayout.Tablet,
						isDesktopLayout: newScreenLayout === ScreenLayout.Desktop,
					},
					this.notifySubscribers
				);
			} else {
				this.notifySubscribers();
			}
		}, RESIZE_BUFFER);
	};

	notifySubscribers() {
		const { subscribers } = this.state;
		each(subscribers, subscriber => subscriber());
	}

	render() {
		const { cls, children, shouldScroll } = this.props;
		return (
			<div
				className={`common-layout viewport ${shouldScroll && isMobile ? 'should-scroll' : ''} ${cls || ''}`}
			>
				<LayoutContext.Provider value={this.state}>
					{children}
				</LayoutContext.Provider>
			</div>
		);
	}
}

Viewport.propTypes = {
	layout: PropTypes.shape({
		north: RegionShape,
		west: RegionShape,
		east: RegionShape,
	}),
};
